import type { BundledLanguage, BundledTheme, HighlighterGeneric } from 'shiki/bundle/web'
import type { Mermaid } from 'mermaid'
import { m } from '#/paraglide/messages'

/**
 * Post-mount enhancements for `<Markdown>` output (client only):
 * - ```mermaid fences → rendered SVG with "source" toggle + copy,
 * - other fences → shiki highlighting (dual light/dark theme via CSS vars) + copy button,
 * Idempotent: safe under React strict mode and re-runs on theme change.
 */

type Opts = { dark: boolean; signal: AbortSignal }
const aborted = (o: Opts) => o.signal.aborted

const SHIKI_LANGS = new Set(['ts', 'typescript', 'tsx', 'js', 'javascript', 'jsx', 'json', 'sql', 'bash', 'sh', 'shell', 'yaml', 'yml', 'md', 'markdown', 'html', 'css', 'python', 'py', 'go', 'rust', 'java', 'kotlin', 'swift', 'dart', 'prisma', 'graphql', 'toml', 'dockerfile'])

let shikiPromise: Promise<HighlighterGeneric<BundledLanguage, BundledTheme>> | null = null
async function getShiki() {
  shikiPromise ??= import('shiki/bundle/web').then((s) => s.createHighlighter({ themes: ['github-light', 'github-dark'], langs: [] }))
  return shikiPromise
}

let mermaidPromise: Promise<Mermaid> | null = null
async function getMermaid() {
  mermaidPromise ??= import('mermaid').then((mod) => mod.default)
  return mermaidPromise
}

let seq = 0
// mermaid.render is not re-entrant — several <Markdown> instances rendering at once must queue
let mermaidQueue: Promise<unknown> = Promise.resolve()
function enqueueMermaid<T>(fn: () => Promise<T>): Promise<T> {
  const next = mermaidQueue.then(fn, fn)
  mermaidQueue = next.catch(() => undefined)
  return next
}
// rendered output caches so re-mounts (HMR, panel toggles, dialogs) paint instantly
const svgCache = new Map<string, string>()
const codeCache = new Map<string, string>()

function copyButton(getText: () => string) {
  const b = document.createElement('button')
  b.type = 'button'
  b.className = 'md-btn'
  b.textContent = m.md_copy()
  b.setAttribute('aria-label', m.md_copy())
  b.addEventListener('click', (e) => {
    e.stopPropagation()
    navigator.clipboard.writeText(getText()).then(() => {
      b.textContent = m.md_copied()
      setTimeout(() => { b.textContent = m.md_copy() }, 1800)
    }).catch(() => {})
  })
  return b
}

/** Wrap every raw <pre data-code> / <pre data-mermaid> once, synchronously (so a second strict-mode run finds them). */
function wrapBlocks(root: HTMLElement) {
  for (const pre of Array.from(root.querySelectorAll<HTMLPreElement>('pre[data-code]'))) {
    const code = pre.querySelector('code')?.textContent ?? ''
    const lang = pre.dataset.lang ?? 'text'
    const wrap = document.createElement('div')
    wrap.className = 'md-code not-prose'
    wrap.dataset.lang = lang
    wrap.dataset.state = 'pending'
    const bar = document.createElement('div')
    bar.className = 'md-bar'
    const label = document.createElement('span')
    label.className = 'md-lang'
    label.textContent = lang
    bar.append(label, copyButton(() => code))
    const body = document.createElement('div')
    body.className = 'md-code-body'
    pre.removeAttribute('data-code')
    pre.replaceWith(wrap)
    body.append(pre)
    wrap.append(bar, body)
    ;(wrap as HTMLElement & { __src?: string }).__src = code
  }
  for (const pre of Array.from(root.querySelectorAll<HTMLPreElement>('pre[data-mermaid]'))) {
    const code = pre.querySelector('code')?.textContent ?? ''
    const wrap = document.createElement('div')
    wrap.className = 'md-mermaid not-prose'
    wrap.dataset.state = 'pending'
    const bar = document.createElement('div')
    bar.className = 'md-bar'
    const label = document.createElement('span')
    label.className = 'md-lang'
    label.textContent = m.md_diagram()
    const srcBtn = document.createElement('button')
    srcBtn.type = 'button'
    srcBtn.className = 'md-btn'
    srcBtn.textContent = m.md_source()
    srcBtn.setAttribute('aria-pressed', 'false')
    srcBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      const showing = wrap.classList.toggle('show-src')
      srcBtn.setAttribute('aria-pressed', String(showing))
    })
    bar.append(label, srcBtn, copyButton(() => code))
    const body = document.createElement('div')
    body.className = 'md-mermaid-body'
    body.textContent = m.md_mermaid_loading()
    const src = document.createElement('div')
    src.className = 'md-mermaid-src'
    pre.removeAttribute('data-mermaid')
    pre.replaceWith(wrap)
    src.append(pre)
    wrap.append(bar, body, src)
    ;(wrap as HTMLElement & { __src?: string }).__src = code
  }
}

async function highlightAll(root: HTMLElement, opts: Opts) {
  const pending = Array.from(root.querySelectorAll<HTMLElement>('.md-code[data-state="pending"]'))
  if (!pending.length) return
  const todo = pending.filter((w) => SHIKI_LANGS.has(w.dataset.lang ?? ''))
  for (const w of pending) if (!todo.includes(w)) w.dataset.state = 'done'
  if (!todo.length) return
  let hl: Awaited<ReturnType<typeof getShiki>>
  try { hl = await getShiki() } catch { for (const w of todo) w.dataset.state = 'done'; return }
  if (aborted(opts)) return
  for (const w of todo) {
    const code = (w as HTMLElement & { __src?: string }).__src ?? ''
    const lang = w.dataset.lang ?? 'text'
    const key = `${lang}\u0000${code}`
    try {
      let html = codeCache.get(key)
      if (!html) {
        if (!hl.getLoadedLanguages().includes(lang)) await hl.loadLanguage(lang as BundledLanguage)
        if (aborted(opts)) return
        html = hl.codeToHtml(code, { lang, themes: { light: 'github-light', dark: 'github-dark' }, defaultColor: false })
        codeCache.set(key, html)
      }
      const body = w.querySelector('.md-code-body')
      if (body) body.innerHTML = html
    } catch { /* unknown language — keep plain */ }
    w.dataset.state = 'done'
  }
}

function paintSvg(w: HTMLElement, svg: string, theme: string) {
  const body = w.querySelector<HTMLElement>('.md-mermaid-body')
  if (!body) return
  body.innerHTML = svg
  body.querySelector('svg')?.removeAttribute('height')
  w.classList.remove('error')
  w.dataset.theme = theme
  w.dataset.state = 'done'
}

async function renderMermaid(root: HTMLElement, opts: Opts) {
  const theme = opts.dark ? 'dark' : 'default'
  const stale = Array.from(root.querySelectorAll<HTMLElement>('.md-mermaid')).filter((w) => w.dataset.theme !== theme || w.dataset.state === 'pending')
  // cache hits paint synchronously; only misses need the (heavy) mermaid import
  const blocks = stale.filter((w) => {
    const code = (w as HTMLElement & { __src?: string }).__src ?? ''
    const hit = svgCache.get(`${theme}\u0000${code}`)
    if (hit) paintSvg(w, hit, theme)
    return !hit
  })
  if (!blocks.length) return
  let mermaid: Awaited<ReturnType<typeof getMermaid>>
  try { mermaid = await getMermaid() } catch { for (const w of blocks) fail(w); return }
  if (aborted(opts)) return
  for (const w of blocks) {
    const code = (w as HTMLElement & { __src?: string }).__src ?? ''
    const body = w.querySelector<HTMLElement>('.md-mermaid-body')
    if (!body) continue
    try {
      const key = `${theme}\u0000${code}`
      const svg = await enqueueMermaid(async () => {
        const hit = svgCache.get(key)
        if (hit) return hit
        mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme, fontFamily: 'inherit' })
        const r = await mermaid.render(`mmd-${Date.now().toString(36)}-${++seq}`, code)
        svgCache.set(key, r.svg)
        return r.svg
      })
      if (aborted(opts)) return
      paintSvg(w, svg, theme)
    } catch {
      if (aborted(opts)) return
      fail(w)
    }
  }
  // mermaid leaves a hidden scratch element around on parse errors
  document.querySelectorAll('[id^="dmmd-"]').forEach((el) => el.remove())
}

function fail(w: HTMLElement) {
  const body = w.querySelector<HTMLElement>('.md-mermaid-body')
  if (body) body.textContent = m.md_mermaid_error()
  w.classList.add('error', 'show-src')
  w.querySelector('.md-btn[aria-pressed]')?.setAttribute('aria-pressed', 'true')
  w.dataset.state = 'done'
  w.dataset.theme = 'error'
}

export async function enhanceMarkdown(root: HTMLElement, opts: Opts) {
  wrapBlocks(root)
  await Promise.all([highlightAll(root, opts), renderMermaid(root, opts)])
}
